/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';

import moment from 'moment';

/* Importing the components from the material-ui library. */
import { Breadcrumbs, Button, Container, Grid, Link, Stack, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

/* Importing functions from the Redux slices. */
import { getPatientAll } from '../../features/patient/patientSlice';
import { getQuestionnaire } from '../../features/questionnaire/questionnaireSlice';
import {
  deleteQuestionnaireResponse,
  searchQuestionnaireResponse,
} from '../../features/questionnaireResponse/questionnaireResponseSlice';

/* Importing the components from the components folder. */
import MaterialTable from '../../components/MaterialTable';
import Page from '../../components/Page';

/* Importing function from the utils folder. */
import deleteResourceHandler from '../../utils/deleteResourceHandler';

/**
 * Renders a list of questionnaire responses.
 *
 * @returns {JSX.Element} The rendered component.
 */
export default function QuestionnaireResponseList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  /* Using the useSelector hook to get the questionnaireResponse state from the Redux store. */
  const { Patients } = useSelector((state) => state.patient);
  const { Questionnaires } = useSelector((state) => state.questionnaire);
  const { searchResults } = useSelector((state) => state.questionnaireResponse);
  const Questionnaire = Questionnaires.find((q) => q.id === params.id);

  /* Setting the states of objects. */
  const [title, setTitle] = useState('Questionnaire Response List');
  const [isLoading, setIsLoading] = useState(true);
  const [QuestionnaireResponsesFormatted, setQuestionnaireResponsesFormatted] = useState([]);

  const findDateInNestedObject = useCallback((obj, linkId) => {
    if (obj.linkId === linkId && obj.answer) {
      return obj.answer[0].valueDate;
    }
    if (Array.isArray(obj.item)) {
      let foundDate = null;
      // eslint-disable-next-line no-return-assign
      obj.item.some((item) => (foundDate = findDateInNestedObject(item, linkId)));
      return foundDate;
    }
    return null;
  }, []);

  const findPatientInfo = useCallback(
    (reference) => {
      const patient = Patients.find((p) => p.id === reference.split('/')[1]);
      return patient;
    },
    [Patients]
  );

  /**
   * Format the questionnaireResponse data to be used in the table.
   */
  const formatQuestionnaireResponses = useCallback(() => {
    const questionnaireResponsesSearch = searchResults.map((p) => p.resource);

    const formattedQuestionnaireResponses = questionnaireResponsesSearch.map((questionnaireResponse) => {
      const formattedQuestionnaireResponse = {
        id: questionnaireResponse.id,
        subject: findPatientInfo(questionnaireResponse.subject.reference).name[0]?.text,
        date: findDateInNestedObject(questionnaireResponse, 'date_of_session'),
      };
      return formattedQuestionnaireResponse;
    });
    setQuestionnaireResponsesFormatted(formattedQuestionnaireResponses);
    setIsLoading(false);
  }, [findDateInNestedObject, findPatientInfo, searchResults]);

  /**
   * Initializes the data for the page.
   */
  const initializeData = useCallback(() => {
    if (Questionnaire) {
      setTitle(`Questionnaire Response List - ${Questionnaire.title}`);
      dispatch(searchQuestionnaireResponse({ query: `questionnaire=${Questionnaire.url}` }));
    }
  }, [Questionnaire, dispatch]);

  useEffect(() => {
    dispatch(getPatientAll());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getQuestionnaire(params.id));
  }, [dispatch, params.id]);

  /* Calling the initializeData function when the component mounts. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  /* Calling the formatQuestionnaireResponses function when the component mounts and when the questionnaireResponseState changes. */
  useEffect(() => {
    formatQuestionnaireResponses();
  }, [formatQuestionnaireResponses]);

  const updateQuestionnaireResponseHandler = (_id) => {
    navigate(`/questionnaireresponse/edit/${params.id}/${_id}`, { replace: false });
  };

  const deleteQuestionnaireResponseHandler = async (id) => {
    const deleted = await deleteResourceHandler({
      dispatch,
      deleteAction: deleteQuestionnaireResponse,
      id,
      successMessage: 'Questionnaire Response deleted successfully!',
    });

    if (deleted) {
      setTimeout(() => {
        dispatch(searchQuestionnaireResponse({ query: `questionnaire=${Questionnaire.url}` }));
      }, 1000);
    }
  };

  /* Creating a table with the following columns: ID, Last Name, Given Name, Suffix, Gender, and
  Birthday. */
  const columns = [
    {
      title: 'ID',
      field: 'id',
    },
    {
      title: 'Subject',
      field: 'subject',
    },
    {
      title: 'Date',
      field: 'date',
    },
  ];

  /**
   * Render the table of questionnaireResponses.
   */
  const renderTable = () => (
    <MaterialTable
      onRowClick={(event, rowData) =>
        navigate(`/questionnaireresponse/details/${params.id}/${rowData.id}`, { replace: false })
      }
      options={{
        search: true,
        exportButton: true,
        exportFileName: moment().format('YYYY-MM-DD HH:mm:ss'),
        actionsColumnIndex: -1,
      }}
      actions={[
        {
          icon: () => <EditIcon />,
          tooltip: 'Edit QuestionnaireResponse',
          onClick: (event, rowData) => updateQuestionnaireResponseHandler(rowData.id),
        },
        {
          icon: () => <DeleteIcon />,
          tooltip: 'Delete QuestionnaireResponse',
          onClick: (event, rowData) => deleteQuestionnaireResponseHandler(rowData.id),
        },
      ]}
      title={''}
      isLoading={isLoading}
      columns={columns}
      data={QuestionnaireResponsesFormatted}
    />
  );

  return (
    <Page title={title}>
      <Container maxWidth="none">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="column" justifyContent="space-between">
            <Typography variant="h4" gutterBottom>
              {title}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" mr={4}>
              <Link underline="hover" color="inherit" component={RouterLink} to="/questionnaire/list">
                Questionnaire
              </Link>
              <Typography color="text.primary">Questionnaire Response</Typography>
            </Breadcrumbs>
          </Stack>

          <Button
            variant="contained"
            component={RouterLink}
            to={`/questionnaireresponse/add/${params.id}`}
            startIcon={<AddIcon />}
          >
            Add Questionnaire Response
          </Button>
        </Stack>

        <Grid container>
          <Grid item xs={12}>
            {renderTable()}
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
